import { useIntl } from "react-intl";
import { DataTableNumberColumn } from "~components/DataTable/components/DataTableNumberColumn/DataTableNumberColumn.tsx";
import { DataTableStringColumn } from "~components/DataTable/components/DataTableStringColumn/DataTableStringColumn.tsx";
import type { DataTableColumn } from "~components/DataTable/types/dataTableColumn.ts";
import type { DataTableRow } from "~components/DataTable/types/dataTableRow.ts";
import type { InfinitySelectOption } from "~components/InfinitySelect/InfinitySelect.tsx";
import SelectCountryCode from "~components/SelectCountryCode/SelectCountryCode.tsx";
import { SelectSite } from "~components/SelectSite/SelectSite.tsx";
import SelectTimezone from "~components/SelectTimezone/SelectTimezone.tsx";
import SelectVenueStatus from "~components/SelectVenueStatus/SelectVenueStatus.tsx";
import { VenueTableColumns } from "~pages/Venues/enums/venueTableColumns.ts";
import { useVenueService } from "~pages/Venues/hooks/useVenueService.ts";
import type { UpdateVenue } from "~pages/Venues/types/upsertVenue.ts";
import type { VenueTransformed } from "~pages/Venues/types/venueTransformed.ts";
import type { LabelValue } from "~types/labelValue.ts";

type Options = {
  massEditMode?: boolean;
};

export const useGetVenueTableColumns = ({ massEditMode = false }: Options = {}): Array<DataTableColumn<VenueTransformed>> => {
  const { formatMessage } = useIntl();
  const { updateVenue } = useVenueService();

  const updateVenueField = async (row: VenueTransformed & DataTableRow, updateData: Omit<UpdateVenue, "venueId">) => {
    row.disabled = true;
    try {
      await updateVenue(row.venueId, updateData);
    } finally {
      row.disabled = false;
    }
  };

  const toLabelValue = (value: string | null | undefined, label: string): LabelValue | undefined => (value ? { value, label } : undefined);

  const columns: Array<DataTableColumn<VenueTransformed>> = [
    {
      key: VenueTableColumns.NameLocal,
      title: formatMessage({ id: "name.local.full" }),
      dataIndex: "nameLocal",
      width: 240,
      ellipsis: { showTitle: true },
      sorter: true,
      render: (value: string, row) => (
        <DataTableStringColumn
          value={value}
          isEditMode={massEditMode}
          disabled={row.disabled}
          required
          onSave={(nameLocal) => updateVenueField(row, { nameLocal })}
        />
      ),
    },
    {
      key: VenueTableColumns.VenueId,
      title: formatMessage({ id: "venue.id" }),
      dataIndex: "venueId",
      copyable: true,
      width: 220,
    },
    {
      key: VenueTableColumns.TimeZone,
      title: formatMessage({ id: "timezone" }),
      dataIndex: "timezone",
      width: 180,
      render: (value: string, row) =>
        massEditMode ? (
          <SelectTimezone
            allowClear={false}
            disabled={row.disabled}
            value={toLabelValue(value, value)}
            onChange={(option) => {
              const timezone = (option as LabelValue)?.value;
              if (!timezone) {
                return;
              }
              void updateVenueField(row, { timezone });
            }}
          />
        ) : (
          value
        ),
    },
    {
      key: VenueTableColumns.CountryCode,
      title: formatMessage({ id: "country" }),
      dataIndex: "countryCode",
      width: 180,
      ellipsis: { showTitle: true },
      render: (value: string | null, row) =>
        massEditMode ? (
          <SelectCountryCode
            disabled={row.disabled}
            style={{ minWidth: "100px", maxWidth: "100%" }}
            value={toLabelValue(value, value ? formatMessage({ id: `countryCode.${value}` }) : "")}
            onChange={(option) => {
              void updateVenueField(row, { countryCode: (option as LabelValue | undefined)?.value ?? null });
            }}
          />
        ) : value ? (
          formatMessage({ id: `countryCode.${value}` })
        ) : (
          ""
        ),
    },
    {
      key: VenueTableColumns.Status,
      title: formatMessage({ id: "status" }),
      dataIndex: "status",
      width: 140,
      render: (value: string | null, row) =>
        massEditMode ? (
          <SelectVenueStatus
            allowClear={false}
            disabled={row.disabled}
            value={toLabelValue(value, value ? formatMessage({ id: value }) : "")}
            onChange={(option) => {
              const status = (option as LabelValue)?.value;
              if (!status) {
                return;
              }
              void updateVenueField(row, { status });
            }}
          />
        ) : value ? (
          formatMessage({ id: value })
        ) : (
          ""
        ),
    },
    {
      key: VenueTableColumns.Site,
      title: formatMessage({ id: "site" }),
      dataIndex: "siteName",
      width: 220,
      ellipsis: { showTitle: true },
      render: (value: string, row) =>
        massEditMode ? (
          <SelectSite
            disabled={row.disabled}
            value={
              row.siteId
                ? {
                    value: row.siteId,
                    label: value || row.site?.nameLocal || "",
                  }
                : null
            }
            onChange={(option: InfinitySelectOption | null) => {
              void updateVenueField(row, { siteId: option?.value ?? null });
            }}
          />
        ) : (
          value
        ),
    },
    {
      key: VenueTableColumns.Capacity,
      title: formatMessage({ id: "capacity" }),
      dataIndex: "capacity",
      width: 120,
      render: (value: number | null, row) => (
        <DataTableNumberColumn
          value={value}
          isEditMode={massEditMode}
          disabled={row.disabled}
          onSave={(capacity) => updateVenueField(row, { capacity })}
        />
      ),
    },
    {
      key: VenueTableColumns.NameLatin,
      title: formatMessage({ id: "name.latin.full" }),
      dataIndex: "nameLatin",
      width: 240,
      ellipsis: { showTitle: true },
      sorter: true,
      render: (value: string | null, row) => (
        <DataTableStringColumn
          value={value}
          isEditMode={massEditMode}
          disabled={row.disabled}
          onSave={(nameLatin) => updateVenueField(row, { nameLatin: nameLatin || null })}
        />
      ),
    },
    {
      key: VenueTableColumns.AbbreviationLocal,
      title: formatMessage({ id: "name.local.abbreviation" }),
      dataIndex: "abbreviationLocal",
      width: 160,
      render: (value: string | null, row) => (
        <DataTableStringColumn
          value={value}
          isEditMode={massEditMode}
          disabled={row.disabled}
          onSave={(abbreviationLocal) => updateVenueField(row, { abbreviationLocal: abbreviationLocal || null })}
        />
      ),
    },
    {
      key: VenueTableColumns.AbbreviationLatin,
      title: formatMessage({ id: "name.latin.abbreviation" }),
      dataIndex: "abbreviationLatin",
      width: 160,
      render: (value: string | null, row) => (
        <DataTableStringColumn
          value={value}
          isEditMode={massEditMode}
          disabled={row.disabled}
          onSave={(abbreviationLatin) => updateVenueField(row, { abbreviationLatin: abbreviationLatin || null })}
        />
      ),
    },
    {
      key: VenueTableColumns.ExternalId,
      title: formatMessage({ id: "externalId" }),
      dataIndex: "externalId",
      copyable: true,
      width: 220,
      render: (value: string | null, row) => (
        <DataTableStringColumn
          value={value}
          isEditMode={massEditMode}
          disabled={row.disabled}
          onSave={(externalId) => updateVenueField(row, { externalId: externalId || null })}
        />
      ),
    },
  ];

  return columns;
};
